import React, { useState } from 'react';
import { Typography, Button, Grid, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import { ISubQuestion } from '../../../../store/StructuredEssayQuestionSlice';
import MarkdownRenderer from '../../../../helper/MarkdownRenderer/MarkdownRenderer';
import AddQuestionModal from './modals/AddQuestionModal';

interface IInitialDetailModalDataState {
  isVisible: boolean;
  question: ISubQuestion | undefined;
  field: 'question' | 'markingScheme';
}

const initialDetailModalDataState: IInitialDetailModalDataState = {
  isVisible: false,
  question: undefined,
  field: 'question',
};

export interface ISubQuestionManager {
  subQuestions: Array<ISubQuestion>;
  deleteOption: (sortingOrder: number) => void;
  addNewOption?: () => void;
  updateOption: (
    sortingOrder: number,
    updatedFields: Partial<ISubQuestion>
  ) => void;
}

const SubQuestionsManager: React.FC<ISubQuestionManager> = ({
  subQuestions,
  deleteOption,
  addNewOption,
  updateOption,
}) => {
  const [
    detailModalData,
    setDetailModalData,
  ] = useState<IInitialDetailModalDataState>(initialDetailModalDataState);

  return (
    <div style={{ marginBottom: '15px' }}>
      <Typography fontWeight={600}>Enter sub questions</Typography>
      <Typography variant="caption" sx={{ mb: '5px' }}>
        Enter sub questions with marking scheme
      </Typography>

      {subQuestions.map((question, index) => {
        const label = `${index + 1})`;

        return (
          <React.Fragment key={index}>
            {/* Question Row */}
            <Grid container spacing={2} mt="10px" alignItems="center">
              <Grid item xs={10}>
                <Typography>{label} Question:</Typography>
              </Grid>
              <Grid item xs={2}>
                <IconButton onClick={() => deleteOption(index)}>
                  <CancelIcon />
                </IconButton>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={12}>
                {question.question.trim() && (
                  <MarkdownRenderer content={question.question} />
                )}
                <Button
                  color="primary"
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                  onClick={() =>
                    setDetailModalData({
                      isVisible: true,
                      question: { ...question, sortingOrder: index },
                      field: 'question',
                    })
                  }
                >
                  {question.question.trim() ? 'Edit' : 'Add'}
                </Button>
              </Grid>
            </Grid>

            {/* Marking Scheme Row */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={12}>
                <Typography>Marking Scheme:</Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={12}>
                {question.markingScheme?.trim() && (
                  <MarkdownRenderer content={question.markingScheme} />
                )}
                <Button
                  color="primary"
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                  onClick={() =>
                    setDetailModalData({
                      isVisible: true,
                      question: { ...question, sortingOrder: index },
                      field: 'markingScheme',
                    })
                  }
                >
                  {question.markingScheme?.trim() ? 'Edit' : 'Add'}
                </Button>
              </Grid>
              <Grid item xs={1} />
            </Grid>

            {/* Marks Row */}
            <Grid container spacing={2} mb="20px" alignItems="center">
              <Grid item xs="auto">
                <Typography>Marks:</Typography>
              </Grid>
              <Grid item xs="auto">
                <input
                  type="number"
                  style={{
                    width: '80px',
                    height: '20px',
                  }}
                  min="0"
                  max="100"
                  value={question.marks || ''}
                  onChange={(e) => {
                    const value = Math.min(
                      Math.max(0, Number(e.target.value)),
                      100
                    );
                    updateOption(index, { marks: value });
                  }}
                />
              </Grid>
            </Grid>
          </React.Fragment>
        );
      })}

      <div>
        <Button
          startIcon={<AddIcon />}
          sx={{ mt: '10px' }}
          onClick={addNewOption}
        >
          Add New
        </Button>
      </div>

      {/* Modal for Question / Marking Scheme */}
      {detailModalData.isVisible && detailModalData.question && (
        <AddQuestionModal
          open={detailModalData.isVisible}
          onClose={() => setDetailModalData(initialDetailModalDataState)}
          question={
            detailModalData.field === 'question'
              ? detailModalData.question.question
              : detailModalData.question.markingScheme || ''
          }
          updateDetails={(markdown) => {
            if (
              detailModalData.question &&
              typeof detailModalData.question.sortingOrder === 'number'
            ) {
              updateOption(detailModalData.question.sortingOrder, {
                [detailModalData.field]: markdown,
              });
            }
            setDetailModalData(initialDetailModalDataState);
          }}
        />
      )}
    </div>
  );
};

export default SubQuestionsManager;
